import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, getDocs, where, Timestamp, limit } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { exportData } from '../../utils/exportUtils';
import { logUserAction } from '../../utils/logger';
import Tooltip from '../common/Tooltip';

const LOG_COLLECTIONS = [
  { key: 'logsCustomer', label: 'Customer Logs' },
  { key: 'logsOpp', label: 'Opportunity Logs' },
  { key: 'logsTask', label: 'Task Logs' },
  { key: 'logsContact', label: 'Contact Logs' },
  { key: 'logsUser', label: 'User Logs' },
  { key: 'logsOrder', label: 'Order Logs' },
  { key: 'logsEmail', label: 'Email Campaign Logs' },
  { key: 'securityLogs', label: 'Security Logs' },
  { key: 'logsCalendar', label: 'Calendar Logs' },
];

const LogExportTab = ({ user, setToast }) => {
  const [selectedCollections, setSelectedCollections] = useState(['logsCustomer']);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format, setFormat] = useState('csv');
  const [maxRecords, setMaxRecords] = useState(5000);
  const [detailLevel, setDetailLevel] = useState('standard');
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [usersMap, setUsersMap] = useState(new Map());

  // Load users map for display name enrichment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const snap = await getDocs(collection(db, collectionPath('users')));
        const map = new Map();
        snap.docs.forEach(d => {
          const data = d.data();
          const name = data.displayName || '';
          if (name) {
            if (data.uid) map.set(data.uid, name);
            if (data.email) {
              map.set(data.email, name);
              map.set(data.email.split('@')[0], name);
            }
          }
        });
        setUsersMap(map);
      } catch (e) {
        console.warn('Failed to load users for export enrichment:', e);
      }
    };
    loadUsers();
  }, []);

  const toggleCollection = (key) => {
    setSelectedCollections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const selectAll = () => {
    setSelectedCollections(LOG_COLLECTIONS.map(c => c.key));
  };

  const selectNone = () => {
    setSelectedCollections([]);
  };

  const fetchLogData = useCallback(async () => {
    const allLogs = [];

    await Promise.all(selectedCollections.map(async (colKey) => {
      const ref = collection(db, collectionPath(colKey));
      const constraints = [orderBy('timestamp', 'desc')];

      if (dateFrom) {
        // Parse as local date (YYYY-MM-DD input) — split to avoid UTC interpretation
        const [fy, fm, fd] = dateFrom.split('-').map(Number);
        const from = new Date(fy, fm - 1, fd, 0, 0, 0, 0);
        constraints.push(where('timestamp', '>=', Timestamp.fromDate(from)));
      }
      if (dateTo) {
        const [ty, tm, td] = dateTo.split('-').map(Number);
        const to = new Date(ty, tm - 1, td, 23, 59, 59, 999);
        constraints.push(where('timestamp', '<=', Timestamp.fromDate(to)));
      }

      constraints.push(limit(maxRecords));

      const q = query(ref, ...constraints);
      const snapshot = await getDocs(q);

      const collectionLabel = LOG_COLLECTIONS.find(c => c.key === colKey)?.label || colKey;

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const ts = data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp ? new Date(data.timestamp) : null);
        const isSecurity = colKey === 'securityLogs';

        // Resolve user display name
        const uid = data.userId || data.actorId || '';
        const email = data.actorEmail || '';
        const rawName = data.userName || data.actorName || '';
        const resolvedName = usersMap.get(uid)
          || usersMap.get(email)
          || (email && usersMap.get(email.split('@')[0]))
          || rawName
          || email
          || '';

        // Resolve entity type/id for security logs  
        const entityType = data.entityType
          || (isSecurity && data.deletedEntity?.entityType ? data.deletedEntity.entityType : '')
          || '';
        const entityId = data.entityId
          || (isSecurity ? (data.deletedEntity?.entityId || data.actorId || '') : '')
          || '';

        allLogs.push({
          id: doc.id,
          collection: collectionLabel,
          timestamp: ts ? ts.toISOString() : '',
          entityType,
          entityId,
          action: data.action || '',
          level: data.level || 'info',
          userId: uid,
          userName: resolvedName,
          changes: data.changes ? JSON.stringify(data.changes) : '',
          details: data.details ? (typeof data.details === 'string' ? data.details : JSON.stringify(data.details)) : '',
          // Detailed fields (always captured, only exported in detailed mode)
          documentId: doc.id,
          customerId: data.associations?.customerId || data.customerId || '',
          opportunityId: data.associations?.opportunityId || data.opportunityId || '',
          contactId: data.associations?.contactId || data.contactId || '',
          taskId: data.associations?.taskId || data.taskId || '',
          orderId: data.associations?.orderId || data.orderId || '',
          associations: data.associations ? JSON.stringify(data.associations) : '',
        });
      });
    }));

    // Sort by timestamp desc
    allLogs.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));

    return allLogs;
  }, [selectedCollections, dateFrom, dateTo, maxRecords, usersMap]);

  const handlePreview = async () => {
    if (selectedCollections.length === 0) {
      setToast({ message: 'Select at least one log collection.', type: 'error' });
      return;
    }

    setExporting(true);
    try {
      const data = await fetchLogData();
      setPreview({
        totalRecords: data.length,
        collections: selectedCollections.length,
        dateRange: dateFrom || dateTo
          ? `${dateFrom || 'start'} to ${dateTo || 'now'}`
          : 'All time',
        sampleRecords: data.slice(0, 5),
      });
    } catch (error) {
      console.error('Preview failed:', error);
      setToast({ message: `Preview failed: ${error.message}`, type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async () => {
    if (selectedCollections.length === 0) {
      setToast({ message: 'Select at least one log collection.', type: 'error' });
      return;
    }

    setExporting(true);
    try {
      const data = await fetchLogData();

      if (data.length === 0) {
        setToast({ message: 'No logs found matching the specified filters.', type: 'error' });
        return;
      }

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `log_export_${timestamp}`;

      if (format === 'json') {
        // JSON export
        const exportObj = {
          exportedAt: new Date().toISOString(),
          exportedBy: user?.displayName || user?.email || '',
          filters: {
            collections: selectedCollections,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            maxRecords,
          },
          totalRecords: data.length,
          records: data,
        };
        const blob = new Blob([JSON.stringify(exportObj, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}.json`;
        a.click();
        URL.revokeObjectURL(url);

        // Audit log the JSON export (non-blocking)
        logUserAction({
          action: 'Data Exported',
          details: `Exported ${data.length} records as JSON: ${filename} — collections: ${selectedCollections.join(', ')}`,
          entityType: 'Log',
          entityId: filename,
        }).catch(err => console.warn('Failed to log JSON export:', err));
      } else {
        // CSV / XLSX via exportUtils
        const isDetailed = detailLevel === 'detailed';
        const headers = isDetailed
          ? ['Timestamp', 'Collection', 'Level', 'Entity Type', 'Entity ID', 'Action', 'User', 'User ID', 'Document ID', 'Customer ID', 'Opportunity ID', 'Contact ID', 'Task ID', 'Order ID', 'Changes', 'Details', 'Associations']
          : ['Timestamp', 'Collection', 'Level', 'Entity Type', 'Entity ID', 'Action', 'User', 'User ID', 'Changes', 'Details'];
        const rowMapper = isDetailed
          ? (row) => [
              row.timestamp,
              row.collection,
              row.level,
              row.entityType,
              row.entityId,
              row.action,
              row.userName,
              row.userId,
              row.documentId,
              row.customerId,
              row.opportunityId,
              row.contactId,
              row.taskId,
              row.orderId,
              row.changes,
              row.details,
              row.associations,
            ]
          : (row) => [
              row.timestamp,
              row.collection,
              row.level,
              row.entityType,
              row.entityId,
              row.action,
              row.userName,
              row.userId,
              row.changes,
              row.details,
            ];
        exportData({
          filename,
          format: format === 'xlsx' ? 'xlsx' : 'csv',
          headers,
          data,
          rowMapper,
          logMeta: {
            entityType: 'Log',
            extra: `collections: ${selectedCollections.join(', ')}`,
          },
        });
      }

      setToast({ message: `Exported ${data.length} log entries as ${format.toUpperCase()}.`, type: 'success' });
    } catch (error) {
      console.error('Export failed:', error);
      setToast({ message: `Export failed: ${error.message}`, type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Log Data</h3>
        <p className="text-sm text-gray-600 mb-6">
          Export logs for external analysis, compliance reporting, or archival. Select collections, apply filters, and choose your format.
        </p>

        {/* Collection Selection */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">Log Collections</label>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-xs text-indigo-600 hover:text-indigo-800">Select All</button>
              <span className="text-xs text-gray-300">|</span>
              <button onClick={selectNone} className="text-xs text-indigo-600 hover:text-indigo-800">Clear</button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {LOG_COLLECTIONS.map(c => (
              <label
                key={c.key}
                className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                  selectedCollections.includes(c.key)
                    ? 'border-indigo-300 bg-indigo-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedCollections.includes(c.key)}
                  onChange={() => toggleCollection(c.key)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">{c.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Format</label>
            <select
              value={format}
              onChange={e => setFormat(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="csv">CSV</option>
              <option value="xlsx">Excel (XLSX)</option>
              <option value="json">JSON</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Detail Level</label>
            <select
              value={detailLevel}
              onChange={e => setDetailLevel(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="standard">Standard</option>
              <option value="detailed">Detailed (with associations)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Max Records</label>
            <select
              value={maxRecords}
              onChange={e => setMaxRecords(parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value={1000}>1,000</option>
              <option value={5000}>5,000</option>
              <option value={10000}>10,000</option>
              <option value={25000}>25,000</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <Tooltip text="Preview record count before exporting">
            <button
              onClick={handlePreview}
              disabled={exporting || selectedCollections.length === 0}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {exporting ? 'Loading...' : '👁️ Preview'}
            </button>
          </Tooltip>
          <Tooltip text={`Export logs as ${format.toUpperCase()}`}>
            <button
              onClick={handleExport}
              disabled={exporting || selectedCollections.length === 0}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {exporting ? 'Exporting...' : `📤 Export as ${format.toUpperCase()}`}
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Preview Results */}
      {preview && (
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex justify-between items-start mb-3">
            <h4 className="font-semibold text-gray-800">Export Preview</h4>
            <button onClick={() => setPreview(null)} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="text-sm">
              <span className="text-gray-500">Total Records:</span>{' '}
              <span className="font-semibold text-gray-900">{preview.totalRecords.toLocaleString()}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Collections:</span>{' '}
              <span className="font-semibold text-gray-900">{preview.collections}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Date Range:</span>{' '}
              <span className="font-semibold text-gray-900">{preview.dateRange}</span>
            </div>
          </div>

          {preview.sampleRecords.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Sample records (first 5):</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-2 py-1 font-medium text-gray-600">Timestamp</th>
                      <th className="text-left px-2 py-1 font-medium text-gray-600">Collection</th>
                      <th className="text-left px-2 py-1 font-medium text-gray-600">Action</th>
                      <th className="text-left px-2 py-1 font-medium text-gray-600">Entity</th>
                      <th className="text-left px-2 py-1 font-medium text-gray-600">User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sampleRecords.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-mono whitespace-nowrap">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '—'}</td>
                        <td className="px-2 py-1">{r.collection}</td>
                        <td className="px-2 py-1">{r.action}</td>
                        <td className="px-2 py-1 font-mono">{r.entityId}</td>
                        <td className="px-2 py-1">{r.userName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-2">📤 Export Information</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <p><strong>CSV:</strong> Best for spreadsheet analysis (Excel, Google Sheets).</p>
          <p><strong>XLSX:</strong> Native Excel format with proper column types.</p>
          <p><strong>JSON:</strong> Best for programmatic analysis. Includes export metadata and filter criteria.</p>
          <p className="text-blue-600 text-xs mt-2">
            Exports include a timestamp and exporter info for compliance audit trails.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LogExportTab;
